import { useAuth } from '@/contexts/auth-context';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingTaxProfileResponseData,
} from '@fluenceur/types';
import { isTaxResidenceCountry } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const BILLING_TAX_PROFILE_QUERY_KEY = ['billingTaxProfile'] as const;

export const billingTaxProfileFieldsSchema = z.object({
  companyName: z.string().trim().min(1).max(255),
  businessType: z.enum(['company', 'individual']),
  registrationNumber: z.string().max(255).nullable(),
  country: z.string().refine(isTaxResidenceCountry),
  addressLine1: z.string().trim().min(1).max(255),
  addressLine2: z.string().max(255).nullable(),
  city: z.string().trim().min(1).max(255),
  postalCode: z.string().trim().min(1).max(20),
  state: z.string().max(255).nullable(),
  vatNumber: z.string().max(64).nullable(),
});

const responseSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    canManage: z.boolean(),
    profile: billingTaxProfileFieldsSchema
      .extend({
        id: z.string().uuid(),
        createdAt: z.string().datetime({ offset: true }),
      })
      .strict()
      .nullable(),
  })
  .strict();

export function readBillingTaxProfile(
  value: unknown
): OrganizationBillingTaxProfileResponseData | undefined {
  const result = responseSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

export function useBillingTaxProfileQuery({
  context,
  enabled = true,
}: {
  context?: OrganizationBillingContext;
  enabled?: boolean;
}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingActionScope();
  const canRead =
    !!scope.userId &&
    !!scope.organizationId &&
    !!scope.brandId &&
    !!context &&
    !!readBillingContext(context) &&
    context.channel === 'stripe' &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    context.capabilities.canReadBilling;
  return useQuery<OrganizationBillingTaxProfileResponseData>({
    queryKey: [...BILLING_TAX_PROFILE_QUERY_KEY, scope.userId, scope.organizationId, scope.brandId],
    enabled: enabled && !isLoading && canRead,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      assertCurrent(scope);
      if (!canRead) throw new Error('No authorized organization tax profile read');
      const response = await api.get<ApiResponse<unknown>>('/dashboard/billing/tax-profile', {
        signal,
      });
      assertCurrent(scope);
      const data = readBillingTaxProfile(response.data.data);
      if (
        response.data.success !== true ||
        !data ||
        data.organizationId !== scope.organizationId ||
        data.brandId !== scope.brandId ||
        (data.canManage && !context?.capabilities.canCommitPaidPlan)
      ) {
        throw new Error('Invalid organization tax profile');
      }
      return data;
    },
  });
}
